import { useCallback } from "react";
import { formatDateAbbrev } from "@src/packages/utils/useDateFormat";
import { View, Text, Pressable, ScrollView } from "react-native";
import { CheckCheck } from "lucide-react-native";
import Animated, { FadeIn, FadeOut, Layout } from "react-native-reanimated";
import { useNavigation } from "@react-navigation/native";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from "@src/packages/care/useNotifications";
import type { Notification } from "@src/packages/care/useNotifications";
import { parseBadgeUnlockEvent } from "@src/packages/badges/badge-events";
import { getNotificationContract } from "@src/packages/notifications/notification-contract";
import { getNotificationRowPresentation } from "@src/packages/notifications/notification-presentation";
import {
  ClinicalDataStatus,
  Dialog,
  DialogHeader,
  DialogIcon,
  EmptyState,
  SectionActionLink,
  ActionBanner,
  ListRowSkeleton,
} from "@louise/moon-ui";
import { EMBER } from "../../../onboarding/theme";
import ThiingsIcon from "../../../shared/ThiingsIcon";
import type { ThiingsIconName } from "../../../shared/icons";
import notificationEmptyIllustration from "../../../assets/images/you-icons/notifications-row-3d.png";
import { useTranslations } from "@src/packages/i18n";
import type { TranslationFunction } from "@src/packages/i18n";
import {
  dispatchNotificationNavAction,
  getNotificationNavAction,
} from "@src/packages/notifications/notification-routing";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type Props = {
  visible: boolean;
  onClose: () => void;
};

function formatRelativeTime(
  t: TranslationFunction,
  timestamp: string,
  dateFmt: (d: Date) => string,
): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs < 0) return t("notifications.time_upcoming");
  if (diffMins < 1) return t("notifications.time_just_now");
  if (diffMins < 60) {
    return t("notifications.time_minutes", {
      params: { minutes: String(diffMins) },
    });
  }
  if (diffHours < 24) {
    return t("notifications.time_hours", {
      params: { hours: String(diffHours) },
    });
  }
  if (diffDays === 1) return t("notifications.time_yesterday");
  if (diffDays < 7) {
    return t("notifications.time_days", {
      params: { days: String(diffDays) },
    });
  }
  return dateFmt(date);
}

// ─── Empty State (no notifications at all) ───────────────────────────────────

function NotificationEmptyState() {
  const t = useTranslations();
  return (
    <EmptyState
      testID="notification-empty-state"
      density="compact"
      illustration={
        <DialogIcon
          source={notificationEmptyIllustration}
          width={68}
          height={80}
          testID="notification-empty-illustration"
        />
      }
      title={t("notifications.empty_title")}
      titleVariant="featured"
      subtitle={t("notifications.empty_subtitle")}
      style={{ paddingHorizontal: 24 }}
    />
  );
}

// ─── All Read Banner (compact, shown above past notifications) ───────────────

function AllReadBanner() {
  const t = useTranslations();
  return (
    <ActionBanner
      testID="notification-all-read-banner"
      variant="encouragement"
      message={t("notifications.all_caught_up")}
      style={{ marginBottom: 12 }}
    />
  );
}

// ─── Error State ─────────────────────────────────────────────────────────────

function ErrorState({
  onRetry,
  isRetrying,
}: {
  onRetry: () => void;
  isRetrying: boolean;
}) {
  const t = useTranslations();
  return (
    <ClinicalDataStatus
      state="unavailable"
      title={t("notifications.load_error")}
      message={t("states.check_connection_and_try_again")}
      retryLabel={t("clinical_data.retry")}
      retryingLabel={t("clinical_data.retrying")}
      onRetry={onRetry}
      isRetrying={isRetrying}
      embedded
      testID="notification-error-state"
    />
  );
}

// ─── Loading State ───────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <View testID="notification-loading-state" style={{ paddingVertical: 8 }}>
      {[1, 2, 3].map((i) => (
        <ListRowSkeleton key={i} showLeadingSquare showSubtitle />
      ))}
    </View>
  );
}

// ─── Notification Row ────────────────────────────────────────────────────────

function NotificationRow({
  notification,
  onPress,
}: {
  notification: Notification;
  onPress: (n: Notification) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("NotificationRow");
  const contract = getNotificationContract(notification.type);
  const presentation = getNotificationRowPresentation(notification);
  const iconName = contract.icon as ThiingsIconName;
  const isRead = !!notification.readAt;
  const badgeEvent = parseBadgeUnlockEvent(notification);
  const quietBadgeEvent = badgeEvent?.event.copy.celebrationMode === "quiet";
  const title = quietBadgeEvent
    ? t("notifications.achievement_is_ready")
    : (badgeEvent?.event.copy.title ?? presentation.title);
  const body = quietBadgeEvent
    ? t("notifications.open_achievements_to_review")
    : (badgeEvent?.event.copy.body ?? presentation.body);
  const metadata = quietBadgeEvent ? [] : presentation.metadata;

  return (
    <Animated.View
      testID={`notification-row-${notification.id}`}
      entering={FadeIn.duration(200)}
      exiting={FadeOut.duration(150)}
      layout={Layout.springify()}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginBottom: 4,
          opacity: isRead ? 0.55 : 1,
          borderRadius: 12,
        }}
      >
        <Pressable
          onPress={() => {
            capturePress(pressLabel(title), "notification_row");
            onPress(notification);
          }}
          accessibilityRole="button"
          accessibilityLabel={title}
          testID={`notification-open-${notification.id}`}
          style={({ pressed }) => ({
            flex: 1,
            flexDirection: "row",
            padding: 14,
            gap: 12,
            backgroundColor: pressed ? "rgba(0,0,0,0.03)" : "transparent",
            borderRadius: 12,
          })}
        >
          {/* Icon */}
          <ThiingsIcon name={iconName} size={28} />

          {/* Content */}
          <View style={{ flex: 1 }}>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                marginBottom: 2,
              }}
            >
              {!isRead && (
                <View
                  testID={`notification-unread-indicator-${notification.id}`}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: EMBER.colors.warm,
                  }}
                />
              )}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  fontWeight: isRead ? "400" : "600",
                  color: EMBER.colors.text,
                  flex: 1,
                }}
                numberOfLines={2}
              >
                {title}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  color: EMBER.colors.textFaint,
                  marginLeft: 4,
                }}
              >
                {formatRelativeTime(
                  t,
                  notification.createdAt,
                  formatDateAbbrev,
                )}
              </Text>
            </View>
            {body ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  marginTop: 2,
                  lineHeight: 18,
                }}
                numberOfLines={2}
              >
                {body}
              </Text>
            ) : null}
            {metadata.length > 0 ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  color: EMBER.colors.textFaint,
                  marginTop: 4,
                }}
                numberOfLines={1}
              >
                {metadata.join(" · ")}
              </Text>
            ) : null}
          </View>
        </Pressable>
      </View>
    </Animated.View>
  );
}

// ─── Main Sheet ──────────────────────────────────────────────────────────────

function NotificationContent({ onClose }: { onClose: () => void }) {
  const t = useTranslations();
  const navigation = useNavigation();
  const {
    data: notifications = [],
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useNotifications({ enabled: true });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const unreadCount = notifications.filter((n) => !n.readAt).length;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleNotificationPress = useCallback(
    (notification: Notification) => {
      // Navigate if the notification type supports it
      const navAction = getNotificationNavAction(notification);
      if (navAction) {
        handleClose();
        // Small delay so close animation plays before routing.
        setTimeout(() => {
          dispatchNotificationNavAction(navigation, navAction);
          if (!notification.readAt) {
            markRead.mutate(notification.id);
          }
        }, 250);
      }
    },
    [markRead, navigation, handleClose],
  );

  const handleMarkAllRead = useCallback(() => {
    if (unreadCount > 0) {
      markAllRead.mutate();
    }
  }, [markAllRead, unreadCount]);

  return (
    <View style={{ flex: 1, minHeight: 0 }}>
      <DialogHeader
        align="center"
        title={
          <Text testID="notification-center-title" accessibilityRole="header">
            {t("notifications.title")}
          </Text>
        }
        style={{ marginBottom: 16 }}
      />

      {unreadCount > 0 && (
        <SectionActionLink
          label={t("notifications.mark_all_read")}
          action="notifications.mark_all_read"
          testID="notification-mark-all-read"
          icon={CheckCheck}
          showChevron={false}
          onPress={handleMarkAllRead}
          disabled={markAllRead.isPending}
          style={{ alignSelf: "flex-end", marginBottom: 10 }}
        />
      )}

      <View style={{ flex: 1, minHeight: 0 }}>
        {isLoading ? (
          <ScrollView showsVerticalScrollIndicator={false}>
            <LoadingSkeleton />
          </ScrollView>
        ) : isError ? (
          <View style={{ flex: 1, justifyContent: "center" }}>
            <ErrorState
              onRetry={() => void refetch()}
              isRetrying={isFetching}
            />
          </View>
        ) : notifications.length === 0 ? (
          <View style={{ flex: 1, justifyContent: "center" }}>
            <NotificationEmptyState />
          </View>
        ) : (
          <ScrollView
            showsVerticalScrollIndicator={false}
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingBottom: 4 }}
          >
            <View
              style={{
                gap: 2,
              }}
            >
              {unreadCount === 0 && <AllReadBanner />}
              {notifications.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onPress={handleNotificationPress}
                />
              ))}
            </View>
          </ScrollView>
        )}
      </View>
    </View>
  );
}

export default function NotificationCenterSheet({ visible, onClose }: Props) {
  if (!visible) return null;

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
      containerStyle={{
        maxHeight: 680,
        minHeight: 520,
        height: "74%",
      }}
      surfaceStyle={{
        height: "100%",
        overflow: "hidden",
      }}
      contentStyle={{
        flex: 1,
        paddingHorizontal: 18,
        paddingBottom: 18,
      }}
      testID="notification-center-dialog"
    >
      <NotificationContent onClose={onClose} />
    </Dialog>
  );
}
